import { GlobalRole, SearchOrder } from '@itm/shared-frontend/lib/types';
import { ResultFailType } from '@itm/shared-frontend';
export { ResultFailType };

export type {
  GlobalRole,
  SelectOption,
  ServerError,
  ServerFormErrors,
  ExternalFilters,
  TanstackTableChangeParams,
} from '@itm/shared-frontend/lib/types';

export enum TargetPlatform {
  AWS = 'AWS',
  AZURE = 'AZURE',
}

export enum Role {
  SuperAdmin = 'SuperAdmin',
  Support = 'Support',
  Analyst = 'EmployerHub_Analyst',
  Viewer = 'EmployerHub_Viewer',
  ClientAdmin = 'EmployerHub_ClientAdmin',
  Manager = 'EmployerHub_Manager',
  CustomerOperations = 'EmployerHub_CustomerOperations',
}

export enum SourceFileStatus {
  FailedValidations = 'FailedValidations',
  CompleteWithWarnings = 'CompleteWithWarnings',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
  /** Assumed value pending backend Swagger; reconcile when EmployerHub API adds it */
  CompleteNoPayrollData = 'CompleteNoPayrollData',
}

export enum PayrollReferenceMigrationStatus {
  InProgress = 'InProgress',
  Completed = 'Completed',
  PartiallyCompleted = 'PartiallyCompleted',
  Failed = 'Failed',
  CompletedWithFailures = 'CompletedWithFailures',
}

export enum ReportStatus {
  Requested = 'Requested',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
}

export enum ReportType {
  TPRReport = 'TPRReport',
  DataChangesExtract = 'DataChangesExtract',
  PensionOfficeDataExtract = 'PensionOfficeDataExtract',
  RestrictedPayrollAccessReport = 'RestrictedPayrollAccessReport',
}

export enum FrequencyType {
  Monthly = 'Monthly',
  Weekly = 'Weekly',
  Fortnightly = 'Fortnightly',
  FourWeekly = 'FourWeekly',
  Quarterly = 'Quarterly',
  BiAnnually = 'BiAnnually',
  Annually = 'Annually',
}

export enum PayrollStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum MembershipStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Pending = 'Pending',
}

export enum UploadAuditStatus {
  Default = 'Default',
  Warning = 'Warning',
  Error = 'Error',
  Success = 'Success',
}

export enum Severity {
  Success = 'Success',
  Warning = 'Warning',
  Error = 'Error',
  Info = 'Info',
}

export enum PayrollValidationSeverity {
  Warning = 'Warning',
  Error = 'Error',
}

export enum ValidationType {
  Max = 'Max',
  Min = 'Min',
  IsNotEmpty = 'IsNotEmpty',
  IsValidDate = 'IsValidDate',
  IsDateMoreThanInYears = 'IsDateMoreThanInYears',
  IsDateBefore = 'IsDateBefore',
  IsDateAfter = 'IsDateAfter',
  IsValidDecimal = 'IsValidDecimal',
  IsValidEmail = 'IsValidEmail',
  IsDependedOn = 'IsDependedOn',
  IsMaxLength = 'IsMaxLength',
  HasChanged = 'HasChanged',
  IsInEnum = 'IsInEnum',
  IsValidRegex = 'IsValidRegex',
  IsFirstNameLessThanLimit = 'IsFirstNameLessThanLimit',
  IsValidTempNiNumber = 'IsValidTempNiNumber',
  IsValidPrevNiNumberPopulated = 'IsValidPrevNiNumberPopulated',
  IsValidPrevNiNumberAndNewValidTemp = 'IsValidPrevNiNumberAndNewValidTemp',
  IsDuplicatePayrollReference = 'IsDuplicatePayrollReference',
  IsDuplicateNiNumber = 'IsDuplicateNiNumber',
  IsMemberMissing = 'IsMemberMissing',
  IsEmployeeContributionsNotPresent = 'IsEmployeeContributionsNotPresent',
  IsValidEspr = 'IsValidEspr',
  IsEmployeeContributionValid = 'IsEmployeeContributionValid',
  IsEmployerContributionValid = 'IsEmployerContributionValid',
  IsContributionAmountsValid = 'IsContributionAmountsValid',
}

export enum PayPeriodStatus {
  NoDataLoaded = 'NoDataLoaded',
  DataLoaded = 'DataLoaded',
  PreppingAdminPlatformData = 'PreppingAdminPlatformData',
  FailedSubmitAdminPlatformData = 'FailedSubmitAdminPlatformData',
  DataSubmittedAdminPlatform = 'DataSubmittedAdminPlatform',
  AwaitingMoney = 'AwaitingMoney',
  DirectDebit = 'DirectDebit',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  DataSubmissionAcceptedOnAdminPlatform = 'DataSubmissionAcceptedOnAdminPlatform',
  /** Assumed value pending backend Swagger; reconcile when EmployerHub API adds it */
  CompletedNoPayrollData = 'CompletedNoPayrollData',
}

export enum ExitReasonCode {
  NotProvided = 'NotProvided',
  EmployeeLeftEmployment = 'EmployeeLeftEmployment',
  CeasedActiveMembership = 'CeasedActiveMembership',
  Death = 'Death',
  Retirement = 'Retirement',
  FlexibleRetirement = 'FlexibleRetirement',
  IllHealthRetirement = 'IllHealthRetirement',
  IntercompanyTransferOut = 'IntercompanyTransferOut',
  OptOut = 'OptOut',
}

export enum TransferOutType {
  PayrollTransfer = 'PayrollTransfer',
  EmployerTransfer = 'EmployerTransfer',
}

export enum EventCode {
  NotAnEnrolmentEvent = 'NotAnEnrolmentEvent',
  AutomaticEnrolment = 'AutomaticEnrolment',
  OptIn = 'OptIn',
  VoluntaryJoiner = 'VoluntaryJoiner',
  ContractualEnrolment = 'ContractualEnrolment',
  JoinRequest = 'JoinRequest',
  CyclicalReEnrolment = 'CyclicalReEnrolment',
  ImmediateReEnrolment = 'ImmediateReEnrolment',
}

export enum RouteAction {
  View = 'view',
  Edit = 'edit',
}

export enum PartTimeType {
  HoursPerWeek = 'HoursPerWeek',
  HoursPerDay = 'HoursPerDay',
  HoursPerMonth = 'HoursPerMonth',
  HoursPerYear = 'HoursPerYear',
  DaysPerWeek = 'DaysPerWeek',
  DaysPerMonth = 'DaysPerMonth',
  DaysPerYear = 'DaysPerYear',
  WeeksPerMonth = 'WeeksPerMonth',
  WeeksPerYear = 'WeeksPerYear',
  MonthsPerYear = 'MonthsPerYear',
}

export enum BreakInServiceType {
  A = 'A',
  LS = 'LS',
  ML = 'ML',
  PL = 'PL',
  S = 'S',
  SA = 'SA',
  SB = 'SB',
  SL = 'SL',
  US = 'US',
  UL = 'UL',
  UML = 'UML',
  AL = 'AL',
  U = 'U',
}

export enum ReportFrequency {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Fortnightly = 'Fortnightly',
  FourWeekly = 'FourWeekly',
  Monthly = 'Monthly',
  Adhoc = 'Adhoc',
}

export enum AuditMethod {
  BulkUpload = 'BulkUpload',
  ManualSalaryDetails = 'ManualSalaryDetails',
  ManualPartTimeDetails = 'ManualPartTimeDetails',
  ManualBreakInServiceDetails = 'ManualBreakInServiceDetails',
  RevertMembershipToActive = 'RevertMembershipToActive',
  ManualEmployeeRequest = 'ManualEmployeeRequest',
  ThirdPartyMemberRequest = 'ThirdPartyMemberRequest',
  BulkUploadEmployeeRequest = 'BulkUploadEmployeeRequest',
  ManualTransferOut = 'ManualTransferOut',
}

export enum FileUploadType {
  DBStandard = 'DBStandard',
  DCStandard = 'DCStandard',
  PAPDIS1_0 = 'PAPDIS1_0',
  PAPDIS1_1 = 'PAPDIS1_1',
  ABFTemplate = 'ABFTemplate',
}

export enum PensionType {
  DB = 'DB',
  DC = 'DC',
}

export enum MembershipRequestStatus {
  Pending = 'Pending',
  Completed = 'Completed',
  Rejected = 'Rejected',
}

export enum ContributionType {
  AnnualYTDEmployee = 'AnnualYTDEmployee',
  Employer = 'Employer',
  Employee = 'Employee',
  AVC = 'AVC',
}
export enum ContributionRateType {
  Contribution = 'Contribution',
  Percentage = 'Percentage',
  Amount = 'Amount',
}

export enum ChaserNotificationType {
  ThirtyDays = '30Days',
  SixtyDays = '60Days',
  NinetyDays = '90Days',
}

export enum ValidationOverrideType {
  Core = 'Core',
  SchemeOverride = 'SchemeOverride',
}

export enum AssessmentCode {
  Excluded = 'Excluded',
  EligibleJobholder = 'EligibleJobholder',
  NonEligibleJobholder = 'NonEligibleJobholder',
  EntitledWorker = 'EntitledWorker',
}

export enum WorkerExclusion {
  OtherOrNotKnown = 'OtherOrNotKnown',
  NotAWorker = 'NotAWorker',
  NotWorkingInTheUK = 'NotWorkingInTheUK',
  WorkingInTheUK = 'WorkingInTheUK',
  OutsideTheAgeRange = 'OutsideTheAgeRange',
  SingleEmployeeOrDirector = 'SingleEmployeeOrDirector',
  CeasedActiveMembershipQPSWithInLast12months = 'CeasedActiveMembershipQPSWithInLast12months',
  CeasedActiveMembershipQPSMoreThan12months = 'CeasedActiveMembershipQPSMoreThan12months',
  WULSWithInLast12months = 'WULSWithInLast12months',
  WULMoreThan12months = 'WULMoreThan12months',
  WorkerInANoticePeriod = 'WorkerInANoticePeriod',
  WorkerWhoHasHMRCTaxProtection = 'WorkerWhoHasHMRCTaxProtection',
}

export enum MessageFunctionCode {
  Enrol = 'Enrol',
  InfoOnly = 'InfoOnly',
  AssessmentRequest = 'AssessmentRequest',
  AssessmentResponse = 'AssessmentResponse',
  WorkerInstruction = 'WorkerInstruction',
}

export enum PaymentMethod {
  DirectDebit = 'DirectDebit',
  NoDirectDebit = 'NoDirectDebit',
}

export enum AdminPlatform {
  PenScope = 'PenScope',
  LumeraCore = 'LumeraCore',
}

export enum EmployerConfigurationStatus {
  PayrollNotCreated = 'PayrollNotCreated',
  PayrollCreated = 'PayrollCreated',
  PayrollNotRequired = 'PayrollNotRequired',
  DirectDebitDetailsNotUpdated = 'DirectDebitDetailsNotUpdated',
  DirectDebitDetailsUpdated = 'DirectDebitDetailsUpdated',
  DirectDebitDetailsNotRequired = 'DirectDebitDetailsNotRequired',
}

export enum AdminSyncAuditArea {
  AdminSynchronisation = 'AdminSynchronisation',
  Payroll = 'Payroll',
}

export enum DirectDebitStatus {
  NotRequired = 'NotRequired',
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
  Manual = 'Manual',
}

export enum AccountValidationStatus {
  Approved = 'Approved',
  Inconclusive = 'Inconclusive',
  ManuallyApproved = 'ManuallyApproved',
  NotValidated = 'NotValidated',
  Partially = 'Partially',
  Rejected = 'Rejected',
}

export enum DataSyncAction {
  Create = 'Create',
  Update = 'Update',
  CreateOrUpdate = 'CreateOrUpdate',
  NoAction = 'NoAction',
}

export enum DataExtractDownloadStatus {
  NoData = 'NoData',
  Restriction = 'Restriction',
  Available = 'Available',
  NotAvailable = 'NotAvailable',
}

export type CompanyResponse = {
  id: string;
  name: string;
};

export type UserPermissionResponse = {
  userId: string;
  companyId: string;
  companyName: string;
  schemeId: string;
  schemeName: string;
  productId: string;
  productName: string;
  productRoleId: string;
  productRoleName: GlobalRole;
  userProductRoleId: string;
  assignmentDate: string;
};

export type DirectDebitContactAddressResponse = {
  address1: string | null;
  address2: string | null;
  address3: string | null;
  address4: string | null;
  address5: string | null;
  postcode: string | null;
};

export type DirectDebitContactPhoneResponse = {
  order: number;
  phoneNumber: string | null;
};

export type DirectDebitContactDetailsResponse = {
  companyName: string | null;
  logoUrl: string | null;
  address: DirectDebitContactAddressResponse;
  email: string | null;
  phoneNumbers: DirectDebitContactPhoneResponse[] | null;
  website: string | null;
};

export type SchemeResponse = {
  id: string;
  companyId: string;
  name: string | null;
  schemeSrcId: string | null;
  fileUploadTypes: FileUploadType[] | null;
};

export type DataExtractResponse = {
  id: string;
  name: string | null;
  type: ReportType;
  status: ReportStatus;
  createdDate: string;
  lastUpdated: string | null;
  createdByName: string | null;
  schemeName: string | null;
  fileSize: number;
  reason: string | null;
  additionalText: string | null;
  downloadStatus: DataExtractDownloadStatus;
};

export type DataExtractResponsePagedResponse = {
  totalCount: number;
  items: DataExtractResponse[];
};

export type SourceFileSearchResponse = {
  id: string;
  name: string;
  fileType: string;
  uploadedDate: string;
  schemeName: string;
  payrollName: string;
  uploadedByUserName: string;
  payrollPeriodEnd: string;
  payrollPeriodStart: string;
  status: string;
  payrollPeriodId: string;
  schemeId: string;
  companyId: string;
  arn: string;
  filePathWithName: string;
  fileNameId: string;
  uploadedByUserEmail: string;
  employerResponses: ShortEmployerResponse[] | null;
  hasDataAccess?: boolean;
};

export type SourceFileHistoryErrors = {
  id: string;
  sourceFileId: string;
  payrollReference: string;
  message: string;
  propertyName: string;
  severity: string;
};

export type SourceFileDetailsDto = {
  id: string;
  name: string;
  fileType: string | null;
  uploadedDate: string;
  uploadedByUserName: string | null;
  status: SourceFileStatus | null;
  runNo: number | null;
  errorMessage: string | null;
};

export type SourceFileSearchResponsePagedResponse = {
  totalCount: number;
  items: SourceFileSearchResponse[];
};

export type PayrollReferenceMigrationHistoryItem = {
  id: string;
  schemeId: string;
  schemeName: string;
  employerId: string;
  employerName: string;
  payrollId: string | null;
  payrollName: string | null;
  requestedByUserName: string;
  requestedDate: string;
  completedDate: string | null;
  status: PayrollReferenceMigrationStatus;
  isReportDownloadAllowed: boolean;
  isReportDownloadAvailable: boolean;
};

export type PayrollReferenceMigrationHistoryItemResponse = {
  items: PayrollReferenceMigrationHistoryItem[];
  maxSize: number;
  totalCount: number;
};

export type ProductInfoResponse = {
  id: string;
  description: string | null;
  version: string | null;
};

export type FileUploadSummary = {
  id: string;
  name: string;
  total: string;
  memberColorClass: string;
};

export type EmployerResponse = {
  id: string;
  name: string | null;
  address1: string | null;
  address2: string | null;
  address5: string | null;
  address4: string | null;
  address3: string | null;
  postcode: string | null;
  email: string | null;
  hasPaymentPlan: boolean;
  updatedDate: string | null;
  createdDate: string;
  epsr: string | null;
  paymentMethod: PaymentMethod;
  adminPlatformSyncEnabled: boolean;
  configurationStatus: string[] | null;
  adminPlatform: AdminPlatform;
  canEnableAdminSync: boolean; //for checkbox enabled/disabled status
  payrollNotRequiredReason: string | null;
  hasMultipleSchemes: boolean;
  shortSchemeResponses: ShortSchemeResponse[] | null;
};

export type EmployerAdminPlatformSummaryResponse = {
  hasEmployersWithoutSyncEnabled: boolean;
  hasLumeraCoreEmployers: boolean;
  hasPenScopeEmployers: boolean;
};

export type PayrollValidationRuleResponsePagedResponse = {
  totalCount: number;
  items: PayrollValidationRuleResponse[];
};

export type PayrollValidationRuleResponse = {
  id: string;
  fieldName: string | null;
  code: string | null;
  validationType: ValidationType;
  validationFileUploadTypes: FileUploadType[] | null;
  validationOverrideType: ValidationOverrideType;
  value: string | null;
  message: string | null;
  resolutionHelper: string | null;
  severity: PayrollValidationSeverity;
  updatedDate: string | null;
  createdDate: string;
  isOptional: boolean;
};

export type ValidationAuditResponse = {
  id: string;
  userId: string;
  payrollValidationRuleId: string;
  userName: string | null;
  oldValue: string | null;
  newValue: string | null;
  createdDate: string;
};

export type ValidationAuditResponsePagedResponse = {
  totalCount: number;
  items: ValidationAuditResponse[] | null;
};

export type PayrollResponse = {
  id: string;
  employerId: string;
  name: string | null;
  employerName: string | null;
  frequencyType: FrequencyType;
  payrollStatus: PayrollStatus;
  payrollStatusName: string | null;
  frequencyTypeName: string | null;
  start: string | null;
  end: string | null;
  cutOffDay: number;
  chaserTriggerDay: number;
  paymentDay: number;
  createdDate: string;
  updatedDate: string | null;
  adminPlatformSyncEnabled: boolean;
  payrollPeriods: PayrollPeriodResponse[] | null;
  payrollRestrictionEnabled: boolean;
  directDebitStatus: DirectDebitStatus;
  shortSchemeResponses: ShortSchemeResponse[] | null;
  payeRef: string | null;
  businessUnit: string | null;
};

export type PayrollPeriodResponse = {
  id: string;
  payrollId: string;
  payrollPeriodEnd: string;
  payrollPeriodStart: string;
  paymentDate: string;
  cutOfDate: string;
  chaserDate: string | null;
  status: PayPeriodStatus;
  statusText: string | null;
  payrollEmployeeData: PayrollEmployeeDataResponse[] | null;
};

export type PayrollEmployeeDataResponse = {
  id: string;
  payrollPeriodId: string;
  payrollReference: string | null;
  dateJoinedCompany: string;
  employeeId: string;
  eeeContRate: number;
  eerContRate: number;
  avcContRate: number;
  contractualMembership: boolean;
  dateJoinedScheme: string;
  dateOfLeaving: string | null;
  employeeConts: number;
  employerConts: number;
  avcConts: number;
  exitReasonCode: ExitReasonCode | null;
  fileUploadType: FileUploadType;
  employee: EmployeeResponse;
};

export type NextPayrollPeriodResponse = {
  id: string;
  payrollId: string;
  payrollName: string | null;
  payrollPeriodStart: string;
  payrollPeriodEnd: string;
  status: PayPeriodStatus;
  frequencyType: FrequencyType;
  isLocked: boolean;
};

export type CreatePayrollModel = {
  employerId: string;
  name: string;
  frequencyType: FrequencyType;
  start: string;
  cutOffDay: number;
  chaserTriggerDay: number;
  paymentDay: number;
  payrollStatus: PayrollStatus;
  payeRef?: string | null;
  businessUnit?: string | null;
};

export type UpdatePayrollModel = {
  id: string;
  employerId: string;
  name: string;
  frequencyType: FrequencyType;
  start?: string;
  end?: string;
  cutOffDay: number;
  chaserTriggerDay: number;
  paymentDay: number;
  payrollStatus: PayrollStatus;
  payeRef?: string | null;
  businessUnit?: string | null;
};

export type SubmitPayrollPeriodDto = {
  sourceFileId: string;
  companyId: string;
};

export type PayrollValidationResponse = {
  id: string;
  employeeId: string;
  payrollReference: string;
  employeeSurname: string;
  employeeNiNumber: string;
  validationStatus: string;
};

export type UploadAuditResponse = {
  id: string;
  stageName: string;
  description: string;
  errorMessage: string;
  timeStamp: string;
  status: UploadAuditStatus;
};

export type DetailedSourceFileResponse = {
  id: string;
  isSourceFileDeleted: boolean;
  name: string | null;
  fileUploadType: FileUploadType;
  uploadedDate: string;
  payrollName: string | null;
  payrollStatus: PayrollStatus | null;
  uploadedByUserName: string | null;
  payrollPeriodEnd: string;
  payrollPeriodStart: string;
  payPeriodStatus: PayPeriodStatus;
  payPeriodStatusText: string | null;
  status: SourceFileStatus;
  payrollPeriodId: string;
  totalRecords: number;
  totalErrors: number;
  totalWarnings: number;
  totalProcessed: number;
  totalEmployeesReadyForProcessing: number | null;
  totalEmployeesIgnoredFromSendingToAdminPlatform: number | null;
  totalEmployeesContributions: number;
  totalAvcEmployeeContributions: number;
  totalEmployersContributions: number;
  totalContributions: number;
  totalContributionsReadyForProcessing: number | null;
  totalContributionsIgnoredFromSendingToAdminPlatform: number | null;
  totalMemberSchemeLeavers: number;
  totalMemberSchemeLeaversFailed: number;
  totalMemberSchemeLeaversProcessed: number;
  totalMemberOptIns: number;
  totalMemberOptInsFailed: number;
  totalMemberOptInsProcessed: number;
  totalMemberOptOuts: number;
  totalMemberOptOutsFailed: number;
  totalMemberOptOutsProcessed: number;
  adminPlatformSyncEnabled: boolean;
  mandate: string | null;
  directDebitStatus: DirectDebitStatus | null;
  dueDate: string | null;
  companyId: string;
  payrollId: string | null;
  isReUploadEnabled: boolean;
  adminPlatform: AdminPlatform;
  employerResponses: ShortEmployerResponse[] | null;
  schemeResponses: ShortSchemeResponse[] | null;
  errorCode: ResultFailType | null;
  sourceFileAudit: PayrollPeriodAuditResponse[];
  canGeneratePensionOfficeReport: boolean;
  rejected: boolean;
  rejectionReason: string | null;
  rejectedByUser: string | null;
  rejectedAt: string | null;
  isSuspectedStuck: boolean;
};

export type SourceFileErrorResponsePagedResponse = {
  totalCount: number;
  items: SourceFileErrorResponse[] | null;
};

export type SourceFileErrorResponse = {
  validationNumber: string | null;
  validationMessage: string | null;
  payrollReference: string | null;
  employeeSurname: string | null;
  employeeNiNumber: string | null;
  validationStatus: Severity;
};

export type SourceFileDownloadAuditResponsePagedResponse = {
  totalCount: number;
  items: SourceFileDownloadAuditResponse[] | null;
};

export type SourceFileDownloadAuditResponse = {
  id: string;
  userName: string | null;
  date: string;
};

export type UpdatePayrollValidationRuleModel = {
  id: string;
  message?: string | null;
  resolutionHelper?: string | null;
  severity: PayrollValidationSeverity;
};

export type PayrollPeriodAuditResponse = {
  stageName: string | null;
  description: string | null;
  errorMessage: string | null;
  updatedDate: string;
  status: UploadAuditStatus;
};

export type MemberSearchResponse = {
  id: string;
  membershipId: string;
  firstName: string | null;
  lastName: string | null;
  nino: string | null;
  dateOfBirth: string | null;
  status: MembershipStatus;
  schemeName: string | null;
  employerName: string | null;
  payrollId: string | null;
  payrollName: string | null;
  payrollReference: string | null;
  isEditEnabled: boolean;
  isEditVisible: boolean;
  isDCOrPAPDIS: boolean;
  isMultipleMembership: boolean;
  pendingOptinRequestPresent: boolean;
  pendingOptOutRequestPresent: boolean;
  pendingSchemeLeaverRequestPresent: boolean;
  membershipRequestTypes: MembershipRequestType[] | null;
};

export type MemberSearchResponsePagedResponse = {
  totalCount: number;
  items: MemberSearchResponse[];
};

export type MembershipDetailDto = {
  companyId?: string | null;
  membershipId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  dateOfBirth: string;
  email?: string | null;
  mobile?: string | null;
};

export type TestChaserNotificationDto = {
  companyId: string;
  employerId: string;
  payrollId: string;
};

export type DisablePayrollDto = {
  employerId: string;
  payrollId: string;
};

export type PayrollResponsePagedResponse = {
  totalCount: number;
  items: PayrollResponse[] | null;
};

export type EmployeeResponse = {
  payrollReference: string | null;
  title: string | null;
  surname: string | null;
  forenames: string | null;
  forename2: string | null;
  gender: string | null;
  dateOfBirth: string | null;
  email: string | null;
  espr: string | null;
  isRemoved: boolean;
  mobile: string | null;
  niNumber: string | null;
  ninoNotApplic: string | null;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  address4: string | null;
  address5: string | null;
  postcode: string | null;
  id: string;
  updatedDate: string | null;
};

export type PersonalDetailsResponse = {
  id: string;
  title: string | null;
  forename: string | null;
  forename2?: string | null;
  surname: string | null;
  dateOfBirth: string;
  niNumber?: string | null;
  email: string | null;
  secondaryEmailAddress?: string | null;
  gender: string | null;
  maritalStatus?: string | null;
  address1: string | null;
  address2: string | null;
  address3?: string | null;
  address4?: string | null;
  address5?: string | null;
  postcode: string | null;
  countryCode?: string | null;
  countryName: string | null;
};

export type MembershipDetailsResponse = {
  memberShipId: string;
  employeeId: string;
  dateJoinedScheme: string;
  dateOfLeaving: string | null;
  exitReasonCode: ExitReasonCode | null;
  fileUploadType: FileUploadType;
  contractualMembership: boolean;
  schemeName: string | null;
  sectionName: string | null;
  membershipRequests: MembershipRequestResponse[] | null;
  reasonForJoining?: EventCode;
  aeOptOutDate: string | null;
  pendingOptinRequestPresent: boolean;
  currentPensionSchemeCode: string | null;
  pensionType: PensionType | null;
  payrollPeriodId: string | null;
  newJoinerFlag: boolean | null;
  niNumber: string | null;
  payrollName: string | null;
};

export type MembershipDetailsResponsePagedResponse = {
  totalCount: number;
  items: MembershipDetailsResponse[] | null;
};

export type GeneralDetailsResponse = {
  employeeId: string;
  payrollReference: string | null;
  dateJoinedCompany: string;
  employer: string | null;
  employerId: string;
  payrollName: string | null;
  payrollId: string;
  schemeId: string;
  salarySacrificeIndicator: boolean;
  salarySacrificeOptInDate: string | null;
  salarySacrificeOptOutDate: string | null;
  salarySacrificeOptOutReason: string | null;
  employmentLocation: string | null;
  originalDateJoinedGroup: string | null;
  currentEmploymentEndDate: string | null;
  interCompanyTransferInDate: string | null;
  nonStandardContributionFlag: boolean | null;
};

export type EmployeePartTimeResponse = {
  id: string;
  partTimeStartDate: string;
  partTimeEndDate: string | null;
  partTimeType: PartTimeType | null;
  currentFTEHours: string | null;
  currentPartTimeHours: number | null;
  currentPartTimePercentage: number | null;
};

export type EmployeePartTimeResponsePagedResponse = {
  totalCount: number;
  items: EmployeePartTimeResponse[];
};

export type CreateEmployeePartTimeModel = {
  employeeId?: string;
  partTimeStartDate: string;
  partTimeEndDate?: string | null;
  partTimeType?: PartTimeType | null;
  currentFTEHours?: string | null;
  currentPartTimeHours?: number | null;
  currentPartTimePercentage?: number | null;
};

export type UpdateEmployeePartTimeModel = {
  id: string;
  employeeId?: string;
  partTimeStartDate: string;
  partTimeEndDate?: string | null;
  partTimeType?: PartTimeType | null;
  currentFTEHours?: string | null;
  currentPartTimeHours?: number | null;
  currentPartTimePercentage?: number | null;
  comment?: string;
};

export type ShortEmployerResponse = {
  id: string;
  name: string | null;
  adminPlatformSyncEnabled: boolean;
};

export type EmployeeBreakInServiceResponse = {
  id: string;
  breakInServiceStartDate: string;
  breakInServiceEndDate: string | null;
  breakInServiceType: BreakInServiceType;
  breakInServiceDescription: string;
};

export type EmployeeBreakInServiceDetailResponse = {
  id: string;
  employeeId: string;
  payrollEmployeeDataId: string;
  breakInServiceStartDate: string;
  breakInServiceEndDate: string | null;
  breakInServiceType: BreakInServiceType;
  breakInServiceDescription: string;
};

export type EmployeeBreakInServiceDetailResponsePagedResponse = {
  totalCount: number;
  items: EmployeeBreakInServiceDetailResponse[];
};

export type CreateBreakInServiceModel = {
  employeeId?: string;
  breakInServiceStartDate: string;
  breakInServiceEndDate?: string | null;
  breakInServiceType: BreakInServiceType;
  breakInServiceDescription: string;
};

export type UpdateBreakInServiceDto = {
  id: string;
  employeeId?: string;
  breakInServiceStartDate: string;
  breakInServiceEndDate?: string | null;
  breakInServiceType: BreakInServiceType;
  breakInServiceDescription: string;
  comment?: string;
};

export type TemplateDefinitionSearchResponse = {
  id: string;
  name: string | null;
  createdDate: string;
  updatedDate: string | null;
  fileUploadTypes: FileUploadType[] | null;
};

export type TemplateDefinitionSearchResponsePagedResponse = {
  totalCount: number;
  items: TemplateDefinitionSearchResponse[];
};

export type SalaryTypesSearchResponse = {
  id: string;
  salaryType: string | null;
  friendlyName: string | null;
  createdDate: string;
  updatedDate: string | null;
};

export type SalaryTypesSearchResponsePagedResponse = {
  totalCount: number;
  items: SalaryTypesSearchResponse[] | null;
};

export type SalaryTypeResponse = {
  id: string;
  salaryType: string;
  friendlyName: string;
};

export type CreateSalaryTypeModel = {
  salaryType: string;
  friendlyName: string;
};

export type UpdateSalaryTypeModel = {
  id: string;
  salaryType: string;
  friendlyName: string;
};

export type EmployeeSalaryDetailResponse = {
  id: string;
  salaryEffectiveStartDate: string;
  salaryType: string;
  value: number;
};

export type EmployeeSalaryDetailResponsePagedResponse = {
  totalCount: number;
  items: EmployeeSalaryDetailResponse[];
};

export type CreateEmployeeSalaryDetailModel = {
  employeeId?: string;
  salaryType: string;
  salaryValue: number;
  salaryEffectiveStartDate: string;
};

export type UpdateEmployeeSalaryDetailModel = {
  id: string;
  employeeId?: string;
  salaryValue: number;
  salaryEffectiveStartDate: string;
  comment?: string;
};

export type ConfigureReportModel = {
  schemeId: string | null;
  reportType: ReportType;
  frequency: ReportFrequency;
};

export type UpdateReportModel = {
  id: string;
  schemeId: string | null;
  frequency: ReportFrequency;
  reportType: ReportType;
};

export type ReportResponse = {
  id: string;
  schemeId: string;
  schemeName: string | null;
  reportType: ReportType;
  frequency: ReportFrequency;
};

export type ConfigurationReportSearchResponsePagedResponse = {
  totalCount: number;
  items: ConfigurationReportSearchResponse[] | null;
};

export type ConfigurationReportSearchResponse = {
  id: string;
  reportType: ReportType;
  schemeName: string | null;
  frequency: ReportFrequency;
  createdDate: string;
  updatedDate: string | null;
  nextRunDate: string;
};

export type MemberAuditSearchResponse = {
  id: string;
  userName: string | null;
  createdDate: string;
  method: AuditMethod;
};

export type MemberAuditSearchResponsePagedResponse = {
  totalCount: number;
  items: MemberAuditSearchResponse[];
};

export type MemberAuditResponse = {
  id: string;
  sourceFileId: string | null;
  payrollReference: string | null;
  companyName: string | null;
  schemeName: string | null;
  employerName: string | null;
  userName: string | null;
  createdDate: string;
  method: AuditMethod;
  comment: string | null;
  previousValues: string | null;
  newValues: string | null;
  connectionSettingName: string | null;
};

export type DataExtractRequestAuditResponsePagedResponse = {
  totalCount: number;
  items: DataExtractRequestAuditResponse[];
};

export type DataExtractRequestAuditResponse = {
  id: string;
  userName: string | null;
  date: string;
};

export type SchemeAuditResponsePagedResponse = {
  totalCount: number;
  items: SchemeAuditResponse[];
};

export type SchemeAuditResponse = {
  id: string;
  userName: string | null;
  createdDate: string;
  payrollReference: string | null;
  schemeName: string | null;
  employerName: string | null;
  schemeId: string;
  method: AuditMethod;
};

export type UpdateUploadTemplateDto = {
  fileUploadTypes: FileUploadType[];
  pensionType?: PensionType;
};

export type UploadTemplateTypeResponse = {
  fileUploadTypes: FileUploadType[] | null;
  pensionType: PensionType;
};

export type SchemeFileUploadTypeResponse = {
  id: string;
  companyId: string;
  name: string | null;
  fileUploadTypes: FileUploadType[] | null;
  pensionType: PensionType | null;
};

export type FileUploadTypeResponse = {
  fileUploadType: FileUploadType;
};

export type MembershipRequestResponsePagedResponse = {
  totalCount: number;
  items: MembershipRequestResponse[];
};

export type MembershipRequestResponse = {
  id: string;
  requestDate: string;
  requestType: MembershipRequestType;
  payrollName: string | null;
  payrollPeriodStart: string;
  payrollPeriodEnd: string;
  status: MembershipRequestStatus;
  createdByName: string | null;
  reason: string | null;
};

export type HasMembershipRequestResponse = {
  hasMembershipRequest: boolean;
  isReportDownloaded: boolean;
};

export type MembershipOptOutResponse = {
  id: string;
  requestDate: string;
  requestType: MembershipRequestType;
  payrollName: string | null;
  payrollPeriodStart: string;
  payrollPeriodEnd: string;
  status: MembershipRequestStatus;
  createdByName: string | null;
  reason: string | null;
};

export type LatePaymentSearchResponsePagedResponse = {
  totalCount: number;
  items: LatePaymentSearchResponse[] | null;
};

export type LatePaymentSearchResponse = {
  payrollPeriodId: string;
  employerName: string | null;
  esprNumber: string | null;
  schemeNames: string[] | null;
  payroll: string | null;
  payrollPeriod: string | null;
  lateDays: number;
  reminderSent: boolean;
  pensionBreach: boolean;
  paymentPlanIndicator: boolean;
};

export type LatePaymentDetailResponse = {
  payrollPeriodId: string;
  employerName: string | null;
  esprNumber: string | null;
  schemeName: string | null;
  charityNumber: string | null;
  companyHouseNumber: string | null;
  employerEmailUsed: string | null;
  paymentPlanIndicator: boolean;
  payrollName: string | null;
  payrollPeriod: string | null;
  payPeriodUploadStatus: string | null;
  contributionDueDate: string | null;
  memberNotified: boolean;
  lateDays: number;
  breachedContract: boolean;
  totalNumberOfAffectedMembers: number;
  chaserSummaryResponses: ChaserSummaryResponse[] | null;
  totalOutstandingContributions: TotalOutstandingContributionResponse;
};

export type ChaserSummaryResponse = {
  chaserType: string | null;
  chaserSentDate: string;
};

export type TotalOutstandingContributionResponse = {
  totalEmployerOutstandingContributions: number;
  totalMemberOutstandingContributions: number;
  totalAdditionalVoluntaryOutstandingContributions: number;
};

export type ContributionAmountResponsePagedResponse = {
  totalCount: number;
  items: ContributionAmountResponse[] | null;
};

export type ContributionAmountResponse = {
  id: string;
  startDate: string;
  contributionType: string | null;
  value: number;
};

export type ContributionRateResponsePagedResponse = {
  totalCount: number;
  items: ContributionRateResponse[] | null;
};

export type ContributionRateResponse = {
  id: string;
  startDate: string;
  contributionType: string | null;
  contributionRateType: ContributionRateType;
  value: number;
};

export type AutoEnrolmentDetailResponse = {
  id: string;
  assessmentCode: AssessmentCode | null;
  workerExclusion: WorkerExclusion | null;
  isIndividualAlreadyMemberOfQPS: boolean;
  deferralDate: string | null;
  aeOptOutReference: string | null;
  optOutWindowEndDate: string | null;
  statutoryLetterCode: string | null;
  enrolmentCommunicationsIssuedDate: string | null;
  messageFunctionCode: MessageFunctionCode | null;
  reEnrolmentIndicator: boolean;
  stagingDate: string | null;
  cyclicalReEnrolmentDate: string | null;
  autoEnrolmentRefundViaPayroll: boolean | null;
};

export type EmployeeRequestModel = {
  employeeId: string;
  requestDate: string;
  requestType: MembershipRequestType;
  reason: string;
};

export type EnableEmployersAdminSyncRequest = {
  employerIds: string[] | null;
};

export type PayrollRequiredDataModel = {
  payrollNotRequired?: boolean;
  reason: string;
};

export enum DataSyncType {
  Payroll = 'Payroll',
  Employer = 'Employer',
  StrategicPartner = 'StrategicPartner',
  DirectDebit = 'DirectDebit',
}

export enum DataSyncStatus {
  NotStarted = 'NotStarted',
  Success = 'Success',
  Failed = 'Failed',
}

export enum PayPeriodEmployeeDataBatchSyncStatus {
  NotStarted = 'NotStarted',
  Awaiting = 'Awaiting',
  Failed = 'Failed',
  Aborted = 'Aborted',
  Cancelled = 'Cancelled',
  Success = 'Success',
}

export enum MembershipRequestType {
  OptOut = 'OptOut',
  SchemeLeaver = 'SchemeLeaver',
  OptIn = 'OptIn',
}

export type DataSyncResponse = {
  id: string;
  createdDate: string;
  employerId: string;
  schemeNames: string | null;
  type: DataSyncType;
  status: DataSyncStatus;
  action: DataSyncAction;
};

export type DataSyncResponsePagedResponse = {
  totalCount: number;
  items: DataSyncResponse[] | null;
};

export type DetailedDataSyncResponse = {
  id: string;
  createdDate: string;
  synchronisationDate: string | null;
  employerId: string;
  schemeName: string | null;
  companyName: string | null;
  employerName: string | null;
  strategicPartnerName: string | null;
  sectionName: string | null;
  payrollName: string | null;
  type: DataSyncType;
  status: DataSyncStatus;
  action: DataSyncAction;
  errorMessage: string | null;
  traceId: string | null;
  syncDetails: string | null;
  directDebitAccountHandlersName: string | null;
};

export type AdminSyncResponse = {
  id: string;
  name: string | null;
  epsr: string | null;
  adminPlatformSyncEnabled: boolean;
  configurationStatus: string[] | null;
  payrollNotRequiredReason: string | null;
  canEnableAdminSync: boolean;
  schemeSectionStrategicPartners: SchemeSectionStrategicPartnerEmployerResponse[] | null;
};

export enum PayrollAuditAction {
  PayrollCreated = 'PayrollCreated',
  PayrollUpdated = 'PayrollUpdated',
  PayrollDisabled = 'PayrollDisabled',
  PayrollRestrictionEnabled = 'PayrollRestrictionEnabled',
  PayrollRestrictionDisabled = 'PayrollRestrictionDisabled',
  GrantedAccess = 'GrantedAccess',
  RevokedAccess = 'RevokedAccess',
  PayrollPeriodCreation = 'PayrollPeriodCreation',
  PayrollPeriodUpdated = 'PayrollPeriodUpdated',
  DirectDebitDetailsCreated = 'DirectDebitDetailsCreated',
  DirectDebitDetailsUpdated = 'DirectDebitDetailsUpdated',
  PayrollPeriodRemoved = 'PayrollPeriodRemoved',
}

export enum CurrentPensionSchemeCode {
  DB = 'DB',
  DC = 'DC',
  NewMember = '0',
}

export type PayrollAuditResponse = {
  id: string;
  userName: string | null;
  createdDate: string;
  payrollAuditAction: PayrollAuditAction;
  newValues: string | null;
  oldValues: string | null;
};

export type PayrollAuditResponsePagedResponse = {
  totalCount: number;
  items: PayrollAuditResponse[] | null;
};

export type AdminSyncAuditResponsePagedResponse = {
  totalCount: number;
  items: AdminSyncAuditResponse[] | null;
};

export type AdminSyncAuditResponse = {
  id: string;
  userName: string | null;
  createdDate: string;
  adminSyncAuditArea: AdminSyncAuditArea;
  description: string | null;
};

export type EmployeeDataSyncResponsePagedResponse = {
  totalCount: number;
  items: EmployeeDataSyncResponse[] | null;
};

export type EmployeeDataSyncResponse = {
  id: string;
  adminPlatform: AdminPlatform;
  createdDate: string;
  updatedDate: string | null;
  status: PayPeriodEmployeeDataBatchSyncStatus;
  errorMessage: string | null;
};

export type ShortUpdatePayrollDto = {
  employerId: string;
  payrollId: string;
};

export type PayrollUserResponsePagedResponse = {
  totalCount: number;
  items: PayrollUserResponse[] | null;
};

export type PayrollUserResponse = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
};

export type AccessRequestModel = {
  employerId: string;
  payrollId: string;
  userId: string;
};

export type ShortDirectDebitResponse = {
  id: string;
  accountHandlersName: string | null;
  bankAccountNumber: string | null;
  sortCode: string | null;
  mandate: string | null;
  isDirectDebitShared: boolean;
  accountValidationStatus: AccountValidationStatus | null;
  reason: string | null;
  createdByName: string | null;
};

export type ShortPayrollEmployerResponse = {
  id: string;
  name: string | null;
  employerName: string | null;
  address1: string | null;
  postcode: string | null;
};

export type ShortPayrollResponse = {
  id: string;
  name: string | null;
  frequencyType: FrequencyType;
};

export type ShortPayrollResponsePagedResponse = {
  totalCount: number;
  items: ShortPayrollResponse[] | null;
};

export type AssignDirectDebitToPayrollsModel = {
  employerId: string;
  payrollIds: string[];
  directDebitId: string;
};

export type CreateDirectDebitModel = {
  employerId: string;
  payrollIds: string[];
  accountHandlersName: string;
  bankAccountNumber: string;
  sortCode: string;
};

export type DirectDebitResponsePagedResponse = {
  totalCount: number;
  items: DirectDebitResponse[] | null;
};

export type DirectDebitResponse = {
  id: string;
  accountHandlersName: string | null;
  bankAccountNumber: string | null;
  sortCode: string | null;
  mandate: string | null;
  createdDate: string;
  updatedDate: string | null;
  directDebitStatus: DirectDebitStatus;
  accountValidationStatus: AccountValidationStatus | null;
};

export type ShortSchemeResponse = {
  id: string;
  name: string | null;
};

export type SearchPayrollResponsePagedResponse = {
  totalCount: number;
  items: ShortPayrollResponse[] | null;
};

export type SearchPayrollResponse = {
  id: string;
  name: string | null;
  frequencyType: FrequencyType;
  schemeId: string;
  schemeName: string | null;
  employerId: string;
  employerName: string | null;
  directDebitStatus: DirectDebitStatus;
  payrollStatus: PayrollStatus;
  payrollStatusName: string | null;
  frequencyTypeName: string | null;
  createdDate: string | null;
  updatedDate: string | null;
  adminPlatformSyncEnabled: boolean;
  payrollRestrictionEnabled: boolean;
  shortSchemeResponses: ShortSchemeResponse[] | null;
};

export type SchemeSectionStrategicPartnerEmployerResponse = {
  schemeId: string;
  schemeName: string | null;
  sectionId: string | null;
  sectionName: string | null;
  strategicPartnerId: string | null;
  strategicPartnerName: string | null;
  isSectionRequired: boolean;
};

export type NewMemberOptInRequestModel = {
  reason: string;
  requestDate: string;
  surname?: string;
  forenames?: string;
  niNumber?: string;
  payrollReference?: string;
  dateOfBirth: string;
  schemeId: string;
  employerId: string;
  payrollId: string;
};

export type NewMemberRequestModel = NewMemberOptInRequestModel & {
  requestType: MembershipRequestType;
  dateJoinedScheme: string;
};

export type NewMemberRequestCheckResponseModel = {
  canProceed: boolean | null;
  confirmationRequired: boolean | null;
  errorMessage: string | null;
  existingEmployeeId: string | null;
};

export type NewMemberRequestResponseModel = {
  newEmployeeCreated: boolean;
  newMemberCreated: boolean;
  membershipId: string | null;
  employeeId: string | null;
  membershipRequestId: string | null;
  errorMessage: string | null;
};

export type RevertMembershipToActiveRequest = {
  membershipId: string;
  payrollPeriodId: string;
  reason: string;
};

export type AddTransferOutDetailsRequest = {
  membershipId: string;
  transferType: TransferOutType;
  dateOfLeaving: string;
  currentEmploymentEndDate?: string;
  reason: string;
};

export type MembershipRequestDetailsResponsePagedResponse = {
  totalCount: number;
  items: MembershipRequestDetailsResponse[];
};

export type MembershipRequestDetailsResponse = {
  id: string;
  payrollReference: string | null;
  niNumber: string | null;
  surname: string | null;
  forenames: string | null;
  requestDate: string;
  payrollPeriodId: string;
  payrollPeriodStart: string;
  payrollPeriodEnd: string;
  requestType: MembershipRequestType;
  payrollName: string | null;
  status: MembershipRequestStatus;
};

export type LockPayrollPeriodDto = {
  companyId: string;
  employerId: string;
  payrollPeriodId: string;
};

export type GeneratePensionOfficeReportDto = {
  companyId: string;
  payrollPeriodId: string;
};

export type MarkPayrollPeriodCompleteNoDataDto = {
  companyId: string;
  payrollId: string;
  payrollPeriodId: string;
  schemeId: string;
  employerId: string;
  fileUploadType: FileUploadType;
};

export type GetPayrollPeriodActiveMembersParams = {
  companyId: string;
  schemeId: string;
  payrollId: string;
};

export type PayrollPeriodActiveMemberResponse = {
  hasActiveMember: boolean;
  numberActiveMember: number;
  payrollReferences: string[];
};

export type PayrollPeriodChangesSummaryReportRequest = {
  companyId: string;
  payrollPeriodId: string;
};

export type CappedContributionResponsePagedResponse = {
  totalCount: number;
  items: CappedContributionResponse[] | null;
};

export type CappedContributionResponse = {
  id: string;
  startDate: string;
  cappedFlag: boolean;
  cappedAmount: number | null;
};

export type RejectSourceFileModel = {
  rejectionReason: string;
};

export type FormattedPayrollPeriodResponse = {
  id: string;
  payrollPeriod: string | null;
};

export type PayrollSummaryReportResponsePagedResponse = {
  totalCount: number;
  items: PayrollSummaryReportResponse[] | null;
};

export type PayrollSummaryReportResponse = {
  sourceFileId: string;
  schemeNames: string[] | null;
  employerName: string | null;
  payrollName: string | null;
  payrollPeriod: string | null;
  memberCount: number;
  totalErrors: number;
  totalWarnings: number;
  totalContributions: number;
  employeeContributions: number;
  employerContributions: number;
  avcContributions: number;
  status: string | null;
  lastUploadDate: string;
};

export type PayrollSummaryDownloadAuditResponsePagedResponse = {
  totalCount: number;
  items: PayrollSummaryDownloadAuditResponse[] | null;
};

export type PayrollSummaryDownloadAuditResponse = {
  id: string;
  userName: string | null;
  date: string;
  filtersApplied: string | null;
};

export enum PayrollReferenceMigrationAvailabilityErrorType {
  None = 'None',
  NoActiveMembers = 'NoActiveMembers',
  MigrationInProgress = 'MigrationInProgress',
  PayrollUploadInProgress = 'PayrollUploadInProgress',
  PayrollRestriction = 'PayrollRestriction',
  DataAccessValidation = 'DataAccessValidation',
  Forbidden = 'Forbidden',
  Unexpected = 'Unexpected',
}

export type PayrollReferenceMigrationAvailabilityResponse = {
  isAvailable: boolean;
  message: string | null;
  errorType: PayrollReferenceMigrationAvailabilityErrorType;
};

export type PayrollReferenceChangeHistoryItem = {
  changedDate: string;
  changedBy: string | null;
  oldPayrollReference: string | null;
  newPayrollReference: string | null;
};

export type PayrollReferenceChangeHistoryItemPagedResponseWithSize = {
  totalCount: number;
  items: PayrollReferenceChangeHistoryItem[] | null;
  maxSize: number;
};

export type ValidationInsightsGranularity = 'Day' | 'Week' | 'Month';

export type ValidationInsightsAnalysisPeriodType = 0 | 1;

export type ValidationInsightsSeverity = 'Error' | 'Warning';

export type ValidationInsightsUploadAttemptsOrderField = 'totalValidations' | 'uploadAttempts';

export type ValidationInsightsQueryParams = {
  FromDate: string;
  ToDate: string;
  EmployerIds?: string[];
  PayrollIds?: string[];
  PayrollPeriodId?: string;
  ValidationCodes?: string[];
  Severity?: ValidationInsightsSeverity;
  AnalysisPeriodType?: ValidationInsightsAnalysisPeriodType;
  Take?: number;
  Skip?: number;
  take?: number;
  skip?: number;
  search?: string;
  Granularity?: ValidationInsightsGranularity;
  orderField?: string;
  orderBy?: SearchOrder | 0 | 1;
};

export type ValidationInsightsEmployerOptionResponse = {
  id: string;
  name: string | null;
};

export type ValidationRuleOption = {
  id: string;
  fieldName: string | null;
  code: string | null;
  validationType: ValidationType;
  validationFileUploadTypes: FileUploadType[] | null;
  validationOverrideType: ValidationOverrideType;
  value: string | null;
  message: string | null;
  resolutionHelper: string | null;
  severity: PayrollValidationSeverity;
  updatedDate: string | null;
  createdDate: string;
  isOptional: boolean;
};

export type ValidationInsightValidationRulesOptionsResponse = ValidationRuleOption[];

export type ValidationInsightsSummaryResponse = {
  totalValidations: number;
  totalErrors: number;
  totalWarnings: number;
  distinctEmployeesAffected: number;
  distinctEmployersImpacted: number;
  distinctPayrollsImpacted: number;
};

export type ValidationInsightsSeverityBreakdownResponse = {
  errorCount: number;
  warningCount: number;
};

export type ValidationInsightsValidationResponse = {
  validationCode: string;
  message: string;
  severity: ValidationInsightsSeverity;
  totalOccurrences: number;
  errorCount: number;
  warningCount: number;
  distinctEmployeesAffected: number;
  distinctUploads: number;
  distinctPayrollPeriodsImpacted: number;
  lastSeenAt: string;
  trend: string;
};

export type ValidationInsightsTrendPointResponse = {
  bucketStart: string;
  validationCode: string;
  totalOccurrences: number;
  errorCount: number;
  warningCount: number;
};

export type ValidationInsightsEmployerComparisonResponse = {
  employerId: string;
  employerName: string;
  payrollId: string;
  payrollName: string;
  totalValidations: number;
  totalErrors: number;
  totalWarnings: number;
  distinctPayrollPeriodsImpacted: number;
};

export type ValidationInsightsEmployerValidationReportResponse = {
  employerId: string;
  employerName: string;
  payrollId: string;
  payrollName: string;
  validationCode: string;
  validationMessage: string;
  severity: ValidationInsightsSeverity;
  count: number;
  employeesImpacted: number;
};

export type ValidationInsightsUploadAttemptsResponse = {
  employerId: string;
  employerName: string;
  payrollId: string;
  payrollName: string;
  payrollPeriodId: string;
  payrollPeriodStart: string;
  payrollPeriodEnd: string;
  uploadAttempts: number;
  finalSuccessfulUploadSequence: number | null;
  finalSuccessfulUploadedDate: string | null;
  totalValidations: number;
  totalErrors: number;
  totalWarnings: number;
};

export type ValidationInsightsEmployerComparisonPagedResponse = {
  totalCount: number;
  items: ValidationInsightsEmployerComparisonResponse[];
};

export type ValidationInsightsEmployerValidationReportPagedResponse = {
  totalCount: number;
  items: ValidationInsightsEmployerValidationReportResponse[];
};

export type ValidationInsightsUploadAttemptsPagedResponse = {
  totalCount: number;
  items: ValidationInsightsUploadAttemptsResponse[];
};

export type ValidationInsightsUploadEfficiencyResponse = {
  bucketStart: string;
  cleanUploads: number;
  warningOnlyUploads: number;
  errorUploads: number;
  finalSuccessfulUploads: number;
};

export type ValidationInsightsClientComparisonResponse = {
  employerId: string;
  employerName: string;
  totalValidations: number;
  totalErrors: number;
  totalWarnings: number;
  totalUploads: number;
  successfulUploads: number;
  averageAttemptsToSuccess: number;
};

export type ValidationInsightsUploadResponse = {
  sourceFileId: string;
  employerId: string;
  employerName: string;
  payrollId: string;
  payrollName: string;
  payrollPeriodId: string;
  payrollPeriodStart: string;
  payrollPeriodEnd: string;
  uploadedDate: string;
  fileUploadType: string;
  sourceFileStatus: string;
  uploadSequence: number;
  isFinalSuccessfulUpload: boolean;
  totalRecords: number;
  totalErrors: number;
  totalWarnings: number;
  totalValidations: number;
  employeesWithErrors: number;
  employeesWithWarnings: number;
};

export type ValidationInsightsValidationPagedResponse = {
  totalCount: number;
  items: ValidationInsightsValidationResponse[];
};

export type ValidationInsightsUploadPagedResponse = {
  totalCount: number;
  items: ValidationInsightsUploadResponse[];
};

export type ValidationInsightsLastUpdatedResponse = {
  lastUpdatedAtUtc: string | null;
};

export type ValidationInsightsPanelKey =
  'Summary' | 'TopValidations' | 'HighestImpactEmployers' | 'EmployerComparison' | 'UploadAttempts' | 'Validations';

export type DashboardViewPanelModel = {
  key: ValidationInsightsPanelKey;
  order: number;
};

export type DashboardViewResponse = {
  id: string;
  name: string;
  order: number;
  panels: DashboardViewPanelModel[];
};

export type DashboardConfigResponse = {
  views: DashboardViewResponse[];
};

export type SaveDashboardConfigRequest = {
  views: DashboardViewResponse[];
};

export type RenameDashboardViewRequest = {
  name: string;
};

export type PanelCatalogueEntryResponse = {
  key: ValidationInsightsPanelKey;
  displayName: string;
  endpointKey: string;
  category: string;
};

export type PanelCatalogueResponse = {
  panels: PanelCatalogueEntryResponse[];
};

export type PanelCatalogueQueryParams = {
  search?: string;
  take?: number;
};
